import React, { useContext, useState } from 'react';
import { ExperienceServiceV2 } from 'core/api/services/experiencesV2';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { AccountBalance, AttachMoney, Filter, InfoOutlined, Place, Rule, Spa } from '@mui/icons-material';
import { getExperienceData } from 'core/api/parser/experience';

const ExperienceWizardContext = React.createContext(null);

export const ExperienceWizardProvider = ({ children }) => {
    const { t } = useTranslation();
    const translate = (key) => t(`experience_wizard_context.${key}`);
    const steps = [
        { id: 1, label: translate('steps.info'), icon: InfoOutlined },
        { id: 2, label: translate('steps.location'), icon: Place },
        { id: 3, label: translate('steps.price_duration'), icon: AttachMoney },
        { id: 4, label: translate('steps.multimedia'), icon: Filter },
        { id: 5, label: translate('steps.policies'), icon: AccountBalance },
        { id: 6, label: translate('steps.includes'), icon: Rule },
        { id: 7, label: translate('steps.eco_message'), icon: Spa },
    ];

    const STEP_KEYS = {
        STEP_1: 'step1',
        STEP_2: 'step2',
        STEP_3: 'step3',
        STEP_4: 'step4',
        STEP_5: 'step5',
        STEP_6: 'step6',
        STEP_7: 'step7',
    };

    const initialStepData = {
        step1: {},
        step2: {},
        step3: {},
        step4: {},
        step5: {},
        step6: {},
        step7: {},
    };
    const [stepData, setStepData] = useState({ ...initialStepData });
    const [activeStep, setActiveStep] = useState(0);
    const [customIncludes, setCustomIncludes] = useState([]);
    const [customNotIncludes, setCustomNotIncludes] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [newExperienceId, setNewExperienceId] = useState(null);

    const updateStepData = (stepKey, data) => {
        if (!stepKey || !Object.values(STEP_KEYS).includes(stepKey)) return;

        if (typeof data !== 'object') return;

        setStepData((prevStepData) => ({
            ...prevStepData,
            [stepKey]: { ...prevStepData[stepKey], ...data },
        }));
    };

    const handleNext = () => {
        setActiveStep((prevActiveStep) => Math.min(prevActiveStep + 1, steps.length - 1));
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => Math.max(prevActiveStep - 1, 0));
    };

    const resetWizard = () => {
        setActiveStep(0);
        setStepData({ ...initialStepData });
        setCustomIncludes([]);
        setCustomNotIncludes([]);
    };

    const addExperience = async (stepsData) => {
        try {
            setSubmitting(true);

            const values = Object.entries(stepsData).reduce((acc, [, value]) => ({ ...acc, ...value }), {});

            const experienceData = getExperienceData({ ...values, currency: values?.currency?.name, customIncludes, customNotIncludes, customEnvPolicies: values?.environmentOptions });

            const result = await ExperienceServiceV2.add(experienceData);

            if (!Number.isInteger(result?.id) || result?.id <= 0) {
                throw new Error(translate('error_adding_experience'));
            }

            setNewExperienceId(result.id);

            return result;
        } catch (error) {
            toast.error(error?.message, {
                position: 'top-right',
                theme: 'colored',
            });

            return false;
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <ExperienceWizardContext.Provider
            value={{
                STEP_KEYS,
                steps,
                stepData,
                activeStep,
                updateStepData,
                handleNext,
                handleBack,
                resetWizard,
                customIncludes,
                setCustomIncludes,
                customNotIncludes,
                setCustomNotIncludes,
                submitting,
                addExperience,
                isSuccess,
                setIsSuccess,
                newExperienceId,
                setNewExperienceId,
            }}
        >
            {children}
        </ExperienceWizardContext.Provider>
    );
};

ExperienceWizardProvider.displayName = 'ExperienceWizardProvider';

export const useExperienceWizard = () => useContext(ExperienceWizardContext);
